/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import { TalerFormAttributes } from "@gnu-taler/taler-util";
import { intervalToDuration, isFuture, isValid, parse } from "date-fns";
import { InternationalizationAPI } from "../../context/translation.js";
import {
  countryNameList,
  countryNationalityList,
} from "../../utils/select-ui-lists.js";
import { UIFormElementConfig } from "../forms-types.js";

function optionalPastDateValidator(
  i18n: InternationalizationAPI,
  limitAge: boolean,
) {
  return (text: string | undefined) => {
    if (!text) return undefined;
    const time = parse(text, "yyyy-MM-dd", new Date());
    if (!isValid(time)) {
      return i18n.fixed({ en: "invalid format", de: "ungültiges Format" });
    }
    if (isFuture(time)) {
      return i18n.fixed({
        en: "it can't be in the future",
        de: "darf nicht in der Zukunft liegen",
      });
    }
    if (limitAge) {
      const { years } = intervalToDuration({ start: time, end: new Date() });
      if (years && years > 120) {
        return i18n.fixed({
          en: "it can't be greater than 120 years",
          de: "darf nicht größer als 120 Jahre sein",
        });
      }
    }
    return undefined;
  };
}

export function vqfStructureIdentityFields(
  i18n: InternationalizationAPI,
  includeDateOfDeath: boolean,
): UIFormElementConfig[] {
  const fields: UIFormElementConfig[] = [
    {
      id: TalerFormAttributes.NAME_OR_ENTITY,
      label: i18n.fixed({
        en: "Last name(s), first name(s)/entity",
        de: "Name(n), Vorname(n)/Firma",
      }),
      type: "text",
      required: true,
    },
    {
      id: TalerFormAttributes.DOMICILE_OR_REGISTERED_OFFICE,
      label: i18n.fixed({
        en: "Actual address of domicile/registered office",
        de: "Effektive Wohnsitzadresse/Sitzadresse",
      }),
      type: "textArea",
      required: true,
    },
    {
      id: TalerFormAttributes.DOMICILE_OR_REGISTERED_OFFICE_COUNTRY,
      label: i18n.fixed({ en: "Country", de: "Land" }),
      type: "selectOne",
      choices: countryNameList(i18n),
      preferredChoiceVals: ["CH"],
      required: true,
    },
    {
      id: TalerFormAttributes.DATE_OF_BIRTH,
      label: i18n.fixed({ en: "Date(s) of birth", de: "Geburtsdatum/-daten" }),
      type: "isoDateText",
      placeholder: "dd/MM/yyyy",
      pattern: "dd/MM/yyyy",
      required: false,
      validator: optionalPastDateValidator(i18n, true),
    },
    {
      id: TalerFormAttributes.NATIONALITY,
      label: i18n.fixed({ en: "Nationality", de: "Nationalität" }),
      type: "selectOne",
      choices: countryNationalityList(i18n),
      preferredChoiceVals: ["CH"],
      required: false,
    },
  ];
  if (includeDateOfDeath) {
    fields.push({
      id: TalerFormAttributes.DATE_OF_DEATH,
      label: i18n.fixed({
        en: "Date of death (if deceased)",
        de: "Todesfall (falls verstorben)",
      }),
      type: "isoDateText",
      placeholder: "dd/MM/yyyy",
      pattern: "dd/MM/yyyy",
      required: false,
      validator: optionalPastDateValidator(i18n, false),
    });
  }
  return fields;
}

export function vqfIdentityList(
  i18n: InternationalizationAPI,
  id: string,
  label: string,
  includeDateOfDeath: boolean,
  required: boolean,
): UIFormElementConfig {
  return {
    id,
    label,
    type: "array",
    labelFieldId: TalerFormAttributes.NAME_OR_ENTITY,
    fields: vqfStructureIdentityFields(i18n, includeDateOfDeath),
    required,
  };
}

export function yesNoChoices(i18n: InternationalizationAPI) {
  return [
    { value: true, label: i18n.fixed({ en: "Yes", de: "Ja" }) },
    { value: false, label: i18n.fixed({ en: "No", de: "Nein" }) },
  ];
}
